import React, { useState, useEffect } from 'react';
import { BookOpen, Calendar, Search, Filter, Heart, DollarSign, Sparkles, Smile, Eye, Brain, ChevronDown, ChevronUp, Loader, Trash2, X, Menu } from 'lucide-react';
import { supabase } from '../shared/supabase';

// ✅ Import centralized components
import Header from '../shared/components/Header';

// ✅ Import centralized theme
import { gradients, buttonStyles, cardStyles, avatarStyles } from '../shared/config/theme';
import { getJourneyConfig } from '../shared/constants/journeyConfig';
import { getTimeGreeting } from '../shared/utils/dateHelpers';

const JournalHistory = ({ user, userProfile, onNavigate, streakCount = 0, isPremium = false }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [expandedEntry, setExpandedEntry] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ show: false, entryId: null });
  const [deleting, setDeleting] = useState(false);

  // ✅ Navigation handlers for Header component
  const handleMenuClick = () => {
    console.log('Menu clicked - navigating to more');
    if (onNavigate) {
      onNavigate('more');
    } else {
      console.error('onNavigate function not provided');
    }
  };

  const handleProgressClick = () => {
    console.log('Progress clicked - navigating to progress');
    if (onNavigate) {
      onNavigate('progress');
    } else {
      console.error('onNavigate function not provided');
    }
  };

  useEffect(() => {
    fetchJournalEntries();
  }, []);

  const fetchJournalEntries = async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('journal_entries')
        .select(`
          id,
          content,
          category,
          word_count,
          created_at,
          is_complete,
          journeys (
            title,
            category
          )
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setEntries(data || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching journal entries:', error);
      setLoading(false);
    }
  };

  const handleDelete = async (entryId) => {
    try {
      setDeleting(true);
      const { error } = await supabase
        .from('journal_entries')
        .delete()
        .eq('id', entryId);

      if (error) throw error;
      setEntries(entries.filter(e => e.id !== entryId));
      setDeleteModal({ show: false, entryId: null });
      setDeleting(false);
    } catch (error) {
      console.error('Error deleting journal entry:', error);
      setDeleting(false);
      alert('Failed to delete journal entry. Please try again.');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric' 
      });
    }
  };

  const getPreview = (content, maxLength = 150) => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + '...';
  };

  const toggleEntry = (entryId) => {
    setExpandedEntry(expandedEntry === entryId ? null : entryId);
  };

  const filteredEntries = entries.filter(entry => {
    const matchesSearch = entry.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         entry.journeys?.title?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || entry.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const getCategoryStats = () => {
    const stats = { all: entries.length };
    entries.forEach(entry => {
      stats[entry.category] = (stats[entry.category] || 0) + 1;
    });
    return stats;
  };

  const categoryStats = getCategoryStats();

  // Get all unique categories from entries for filters
  const categories = ['confidence', 'love', 'abundance', 'healing', 'gratitude', 'focus', 'clarity', 'joy', 'courage'];

  if (loading) {
    return (
      <div className={`min-h-screen bg-gradient-to-br ${gradients.background} flex items-center justify-center`}>
        <div className="text-center">
          <Loader className="w-12 h-12 text-orange-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading your journal entries...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-gradient-to-br ${gradients.background} pb-24`}>
      {/* Delete Confirmation Modal */}
      {deleteModal.show && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`${cardStyles.default.background} rounded-3xl p-8 max-w-md w-full shadow-2xl`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-800">Delete Journal Entry?</h3>
              <button
                onClick={() => setDeleteModal({ show: false, entryId: null })}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <p className="text-gray-600 mb-6">
              This will permanently delete this journal entry. This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteModal({ show: false, entryId: null })}
                disabled={deleting}
                className={`flex-1 ${buttonStyles.secondary.base} ${buttonStyles.secondary.hover} ${buttonStyles.secondary.active} ${buttonStyles.secondary.transition} ${buttonStyles.secondary.rounded} py-3 px-4 disabled:opacity-50`}
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteModal.entryId)}
                disabled={deleting}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white font-semibold py-3 px-4 rounded-xl transition-all disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg"
              >
                {deleting ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✅ Centralized Header Component with Fixed Navigation */}
      <Header
        user={user}
        userProfile={userProfile}
        isPremium={isPremium}
        streakCount={streakCount}
        onMenuClick={handleMenuClick}
        onProgressClick={handleProgressClick}
        showProgress={!!user}
        subtitle="Reflect on your journey"
      />

      {/* Search and Filters Bar */}
      <div className="bg-white/80 backdrop-blur-md border-b border-peach-200/50 px-6 py-4 sticky top-[72px] z-10">
        <div className="max-w-6xl mx-auto flex gap-4 items-center">
          <div className="flex-1 relative">
            <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search journal entries..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-400 transition-all"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 text-orange-600 hover:text-orange-700 font-medium transition-colors text-sm"
          >
            <Filter className="w-4 h-4" />
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="bg-white/80 backdrop-blur-md border-b border-peach-200/50 px-6 py-4">
          <div className="max-w-6xl mx-auto">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Filter by Journey</h3>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSelectedCategory('all')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  selectedCategory === 'all'
                    ? `bg-gradient-to-r ${gradients.button} text-white shadow-md`
                    : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
                }`}
              >
                All ({categoryStats.all || 0})
              </button>
              {categories.map(category => {
                const config = getJourneyConfig(category);
                return (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`px-4 py-2 rounded-full text-sm font-medium capitalize transition-all ${
                      selectedCategory === category
                        ? `bg-gradient-to-r ${config.gradient} text-white shadow-md`
                        : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {category} ({categoryStats[category] || 0})
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Stats Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className={`${cardStyles.default.background} ${cardStyles.default.rounded} ${cardStyles.default.shadow} ${cardStyles.default.padding} ${cardStyles.default.border}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Total Entries</p>
                <p className="text-3xl font-bold text-gray-800 mt-1">{entries.length}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-orange-100 to-peach-100 rounded-xl flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </div>

          <div className={`${cardStyles.default.background} ${cardStyles.default.rounded} ${cardStyles.default.shadow} ${cardStyles.default.padding} ${cardStyles.default.border}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Total Words</p>
                <p className="text-3xl font-bold text-gray-800 mt-1">
                  {entries.reduce((sum, entry) => sum + (entry.word_count || 0), 0).toLocaleString()}
                </p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-cream-100 to-peach-100 rounded-xl flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </div>

          <div className={`${cardStyles.default.background} ${cardStyles.default.rounded} ${cardStyles.default.shadow} ${cardStyles.default.padding} ${cardStyles.default.border}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">This Month</p>
                <p className="text-3xl font-bold text-gray-800 mt-1">
                  {entries.filter(e => {
                    const date = new Date(e.created_at);
                    const now = new Date();
                    return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
                  }).length}
                </p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-peach-100 to-orange-100 rounded-xl flex items-center justify-center">
                <Calendar className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Journal Entries List */}
        {filteredEntries.length === 0 ? (
          <div className={`${cardStyles.default.background} ${cardStyles.default.rounded} ${cardStyles.default.shadow} ${cardStyles.default.border} p-12 text-center`}>
            <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-gray-700 text-xl font-semibold mb-2">
              {searchQuery || selectedCategory !== 'all' 
                ? 'No entries found' 
                : 'No journal entries yet'}
            </h3>
            <p className="text-gray-500">
              {searchQuery || selectedCategory !== 'all'
                ? 'Try adjusting your filters or search query'
                : 'Start your first manifestation journey to create journal entries'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredEntries.map((entry) => {
              // ✅ Use centralized journey config
              const config = getJourneyConfig(entry.category);
              const Icon = config.icon;
              const isExpanded = expandedEntry === entry.id;

              return (
                <div
                  key={entry.id}
                  className={`${cardStyles.default.background} rounded-3xl shadow-md border-2 ${config.borderColor} overflow-hidden transition-all hover:shadow-lg`}
                >
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 bg-gradient-to-br ${config.gradient} rounded-xl flex items-center justify-center shadow-sm`}>
                          <Icon className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <h3 className="text-gray-800 font-bold capitalize">{entry.category}</h3>
                          <p className="text-gray-500 text-sm flex items-center gap-2">
                            <Calendar className="w-3 h-3" />
                            {formatDate(entry.created_at)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <p className="text-gray-600 text-sm font-medium">{entry.word_count || 0} words</p>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteModal({ show: true, entryId: entry.id });
                          }}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete entry"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    <div className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                      {isExpanded ? entry.content : getPreview(entry.content)}
                    </div>

                    {entry.content.length > 150 && (
                      <button
                        onClick={() => toggleEntry(entry.id)}
                        className={`mt-4 flex items-center gap-1 ${config.textColor} hover:opacity-75 font-medium text-sm transition-all`}
                      >
                        {isExpanded ? (
                          <>
                            <ChevronUp className="w-4 h-4" />
                            Show Less
                          </>
                        ) : (
                          <>
                            <ChevronDown className="w-4 h-4" />
                            Read More
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default JournalHistory;